/**
 * Routes API service — wraps the existing /sales-routes and /delivery-routes
 * endpoints and adds typed helpers for driver/sales-rep self views.
 */
import { apiClient } from './api';

export interface SalesRoute {
  id: number;
  route_name: string;
  assigned_sales_rep_id: number | null;
  store_sequence: any;
  optimization_level: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface DeliveryRoute {
  id: number;
  route_name: string;
  assigned_driver_id: number | null;
  delivery_sequence: any;
  delivery_time_windows: any;
  scheduled_date: string | null;
  status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface RouteEfficiencyMetric {
  id: number;
  route_id: number;
  route_type: string;
  metric_date: string;
  revenue_generated: number;
  miles_driven: number;
  stops_completed: number;
  revenue_per_mile: number;
  stops_per_day: number;
  total_time_minutes: number;
  average_time_per_stop_minutes: number;
  completion_status: string;
  anomalies_detected: any;
}

function unwrapList<T>(raw: any, key: string): T[] {
  if (Array.isArray(raw)) return raw as T[];
  if (Array.isArray(raw?.data)) return raw.data as T[];
  if (Array.isArray(raw?.[key])) return raw[key] as T[];
  return [];
}

export async function listSalesRoutes(): Promise<SalesRoute[]> {
  const raw = await apiClient.get<any>('/sales-routes');
  return unwrapList<SalesRoute>(raw, 'routes');
}

export async function listDeliveryRoutes(filters: { status?: string; date?: string } = {}): Promise<DeliveryRoute[]> {
  const raw = await apiClient.get<any>('/delivery-routes', filters);
  return unwrapList<DeliveryRoute>(raw, 'routes');
}

export async function getDeliveryRoute(id: number | string): Promise<DeliveryRoute> {
  return apiClient.get<DeliveryRoute>(`/delivery-routes/${id}`);
}

export async function getMyDriverRoute(): Promise<DeliveryRoute | null> {
  try {
    // Backend returns a DriverRouteView ({route_id, route_name, status,
    // scheduled_date, stops, …}) instead of the bare delivery_routes row.
    // Normalize to the DeliveryRoute shape the routes store expects.
    const raw = await apiClient.get<any>('/drivers/me/route');
    if (!raw) return null;
    return {
      id: raw.id ?? raw.route_id,
      route_name: raw.route_name,
      assigned_driver_id: raw.assigned_driver_id ?? null,
      delivery_sequence: raw.delivery_sequence ?? raw.stops ?? [],
      delivery_time_windows: raw.delivery_time_windows ?? [],
      scheduled_date: raw.scheduled_date ?? null,
      status: raw.status,
      is_active: raw.is_active ?? true,
      created_at: raw.created_at ?? '',
      updated_at: raw.updated_at ?? '',
    } as DeliveryRoute;
  } catch {
    return null;
  }
}

export async function listRouteMetrics(filters: { from?: string; to?: string; routeType?: string } = {}): Promise<RouteEfficiencyMetric[]> {
  const raw = await apiClient.get<any>('/route-metrics', filters);
  return unwrapList<RouteEfficiencyMetric>(raw, 'metrics');
}

export async function assignDeliveryDriver(routeId: number | string, driverId: number): Promise<DeliveryRoute> {
  const raw = await apiClient.patch<any>(`/delivery-routes/${routeId}/assign`, { driver_id: driverId });
  return (raw?.data ?? raw) as DeliveryRoute;
}

export type DeliveryRouteStatus = 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';

export async function updateDeliveryRouteStatus(
  routeId: number | string,
  status: DeliveryRouteStatus,
): Promise<DeliveryRoute> {
  const raw = await apiClient.patch<any>(`/delivery-routes/${routeId}/status`, { status });
  return (raw?.data ?? raw) as DeliveryRoute;
}

export interface CreateDeliveryRouteInput {
  route_name: string;
  scheduled_date?: string;     // ISO date (YYYY-MM-DD)
  driver_id?: number | null;
  delivery_sequence?: any[];   // optional pre-baked stop list; admin can add stops after creation
  delivery_time_windows?: any[];
  optimization_level?: 'manual' | 'shortest' | 'fastest';
}

export async function createDeliveryRoute(input: CreateDeliveryRouteInput): Promise<DeliveryRoute> {
  const raw = await apiClient.post<any>('/delivery-routes', input);
  return (raw?.data ?? raw) as DeliveryRoute;
}

export interface DeliveryStop {
  id: number;
  delivery_route_id: number;
  order_id: number;
  customer_id: number | null;
  customer_name: string | null;
  sequence: number;
  status: 'pending' | 'in_transit' | 'delivered' | 'failed' | 'skipped';
  scheduled_window_start: string | null;
  scheduled_window_end: string | null;
  completed_at: string | null;
}

export interface CreateStopInput {
  order_id: number;
  sequence: number;
  scheduled_window_start?: string;
  scheduled_window_end?: string;
  notes?: string;
}

export async function listRouteStops(routeId: number | string): Promise<DeliveryStop[]> {
  const raw = await apiClient.get<any>(`/delivery-routes/${routeId}/stops`);
  return unwrapList<DeliveryStop>(raw, 'stops');
}

export async function addStopToRoute(routeId: number | string, input: CreateStopInput): Promise<DeliveryStop> {
  return apiClient.post<DeliveryStop>(`/delivery-routes/${routeId}/stops`, input);
}
